import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../../../ai-chat/styles/aiPopupWindow.css';
import type { AIProvider } from '@xiaotianshu/shared/ai';
import type { Chapter, EditorSelectionContext } from '../../../../types';
import type { Editor } from '@tiptap/react';
import { WritingMode } from './WritingMode';
import { useQuickTools, type QuickToolKind } from './QuickTools';
import { buildPlotPrompt, buildPolishPrompt, buildSummaryPrompt, buildInspirationPrompt, buildQuickToolTitle, buildQuickToolDisplay, type PolishMode } from '../../../../ai/quickTools';

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = (): void => undefined;

// -- Panel Props ----------------------------------------------------------

interface AIAssistantPanelProps {
  open: boolean;
  onClose: () => void;
  bookId: string;
  chapter: Chapter | null;
  editor: Editor | null;
  onAutoSave?: () => void;
  onEditorLock?: (locked: boolean) => void;
  onRequestChapterSwitch?: (chapterId: string) => Promise<boolean>;
  /** 章节结构（卷/章增删、标题/字数变化）后刷新侧边栏 */
  onChapterListRefresh?: () => void;
  /** 资料库（人物/势力/设定/物品/大纲）变化后刷新面板 */
  onWorldRefresh?: () => void;
  /** 右键润色请求（编辑器右键菜单 → 进入润色对话） */
  polishRequest?: { selection: EditorSelectionContext; mode: 'polish' | 'condense' | 'expand'; nonce: number } | null;
  /** 右键"添加到助手"的位置引用列表（仅存位置，AI 自行查询定位） */
  assistantRefs?: EditorSelectionContext[];
  onRemoveAssistantRef?: (index: number) => void;
  onClearAssistantRefs?: () => void;
}

// -- Main Component -------------------------------------------------------

const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({
  bookId, chapter, editor,
  onAutoSave, onEditorLock, onRequestChapterSwitch,
  onChapterListRefresh, onWorldRefresh,
  polishRequest,
  assistantRefs, onRemoveAssistantRef, onClearAssistantRefs,
}) => {
  // -- Agent status --
  const [agentStatus, setAgentStatus] = useState<string | null>(null);
  // -- 会话历史视图（顶部"会话历史"按钮） --
  const [historyOpen, setHistoryOpen] = useState(false);

  // -- Ref to the panel container --
  const panelRef = useRef<HTMLDivElement>(null);

  // -- Read current model from localStorage --
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<AIProvider>('deepseek');

  // -- Handler for WritingMode to request model change --
  // 模型偏好的持久化由 WritingMode 负责（按供应商记录），此处只同步状态，避免覆盖 map。
  const handleWritingModelChange = useCallback((model: string, provider: string) => {
    setSelectedModel(model);
    setSelectedProvider(provider as AIProvider);
  }, []);

  // -- Init: read model preference --
  useEffect(() => {
    const syncPref = () => {
      try {
        const localPref = localStorage.getItem('AI_MODEL_PREFERENCE');
        if (localPref) {
          const p = JSON.parse(localPref) as { modelId?: string; provider?: string; selected?: Record<string, string> };
          if (p.selected && typeof p.selected === 'object') {
            const prov = (p.provider as AIProvider) || 'deepseek';
            setSelectedProvider(prov);
            setSelectedModel(p.selected[prov] ?? '');
          } else if (p.modelId && p.provider) {
            setSelectedModel(p.modelId);
            setSelectedProvider(p.provider as AIProvider);
          }
        }
      } catch { /* ignore */ }
    };
    syncPref();

    window.api.getUserPreference('AI_MODEL_PREFERENCE').then((pref) => {
      if (pref?.preference_value) {
        try {
          const p = JSON.parse(pref.preference_value) as { modelId?: string; provider?: string; selected?: Record<string, string> };
          if (p.selected && typeof p.selected === 'object') {
            const prov = (p.provider as AIProvider) || 'deepseek';
            setSelectedProvider(prov);
            setSelectedModel(p.selected[prov] ?? '');
          } else if (p.modelId && p.provider) {
            setSelectedModel(p.modelId);
            setSelectedProvider(p.provider as AIProvider);
          }
          localStorage.setItem('AI_MODEL_PREFERENCE', pref.preference_value);
        } catch { /* ignore */ }
      }
    }).catch(() => { /* ignore */ });
  }, []);

  // -- Clear state on book switch --
  useEffect(() => {
    setAgentStatus(null);
    setHistoryOpen(false);
  }, [bookId]);

  // -- 轻量 AI 工具（对话式）：卡文急救 / 润色 / 章节摘要 / 灵感点子 --
  const quickTools = useQuickTools();
  // enterTool/exitTool 为稳定引用（useCallback 空依赖），避免父组件回调每次渲染变化
  const { enterTool, exitTool } = quickTools;

  // -- 调试模式：开启后会话页展示完整提示词（模型设置面板可切换，localStorage 记忆） --
  const [debugMode, setDebugMode] = useState(() => {
    try { return localStorage.getItem('AI_DEBUG_MODE') === '1'; } catch { return false; }
  });
  const handleDebugModeChange = useCallback((on: boolean) => {
    setDebugMode(on);
    try { localStorage.setItem('AI_DEBUG_MODE', on ? '1' : '0'); } catch { /* ignore */ }
  }, []);

  // -- 对话活跃状态：对话关闭（标题栏 X）时退出工具对话，恢复完整工具行 --
  const handleConversationActive = useCallback((active: boolean) => {
    if (!active) exitTool();
  }, [exitTool]);

  // -- 工具对话：构造指令（inst 发 AI / display 展示文本 / title 固定标题）并自动发送 --
  const [autoSend, setAutoSend] = useState<{ inst: string; display: string; title: string; refs?: EditorSelectionContext[] } | null>(null);

  const startTool = useCallback(async (kind: QuickToolKind, polishMode?: PolishMode, selectionOverride?: EditorSelectionContext) => {
    if (!selectedModel) return;
    enterTool(kind);
    let inst = '';
    let refs: EditorSelectionContext[] | undefined;
    if (kind === 'plot') {
      // 让 AI 通过 query 工具自主拉取大纲与当前章节内容
      inst = buildPlotPrompt();
    } else if (kind === 'polish' && polishMode) {
      // 润色只告知文字位置（章节 + 段落范围），AI 自主查询定位；右键菜单传入的选区必带
      if (!selectionOverride) { exitTool(); return; }
      inst = buildPolishPrompt({ mode: polishMode, selection: selectionOverride });
      refs = [selectionOverride];
    } else if (kind === 'summary') {
      // 让 AI 用 query 读正文，生成后用 execute 更新摘要（走审批）
      inst = buildSummaryPrompt();
    } else if (kind === 'inspiration') {
      // 让 AI 通过 query 工具自主拉取大纲
      inst = buildInspirationPrompt();
    }
    if (!inst.trim()) { exitTool(); return; }
    // display：调试模式展示完整提示词，否则展示友好文本
    setAutoSend({
      inst,
      display: debugMode ? inst : buildQuickToolDisplay(kind, polishMode),
      title: buildQuickToolTitle(kind, chapter?.title),
      refs,
    });
  }, [debugMode, enterTool, exitTool, selectedModel, chapter]);

  // 右键润色请求：进入润色对话（使用右键菜单传入的选区文本）
  const handledPolishNonce = useRef(0);
  useEffect(() => {
    if (!polishRequest || polishRequest.nonce === handledPolishNonce.current) return;
    handledPolishNonce.current = polishRequest.nonce;
    void startTool('polish', polishRequest.mode, polishRequest.selection);
  }, [polishRequest, startTool]);

  return (
    <div className="cm-ai-assistant-panel" ref={panelRef}>
      <div className="cm-ai-view-fade">
        {/* Content area */}
        <div className="cm-ai-panel-content">
          <WritingMode
            workId={bookId}
            chapter={chapter}
            editor={editor}
            agentStatus={agentStatus}
            setAgentStatus={setAgentStatus}
            onAutoSave={onAutoSave ?? noop}
            onEditorLock={onEditorLock ?? noop}
            onRequestChapterSwitch={onRequestChapterSwitch}
            onChapterListRefresh={onChapterListRefresh}
            onWorldRefresh={onWorldRefresh}
            selectedModel={selectedModel}
            selectedProvider={selectedProvider}
            onConversationActive={handleConversationActive}
            onModelChange={handleWritingModelChange}
            externalConvId={null}
            onExternalConvLoaded={noop}
            onConversationsUpdated={noop}
            hideConvList={true}
            historyOpen={historyOpen}
            onHistoryClose={() => setHistoryOpen(false)}
            onToggleHistory={() => setHistoryOpen(v => !v)}
            onQuickTool={(kind) => void startTool(kind)}
            onExitConversation={undefined}
            onConvCreated={undefined}
            assistantRefs={assistantRefs}
            onRemoveAssistantRef={onRemoveAssistantRef}
            onClearAssistantRefs={onClearAssistantRefs}
            debugMode={debugMode}
            onDebugModeChange={handleDebugModeChange}
            autoSendInstruction={autoSend}
            onAutoSendConsumed={() => setAutoSend(null)}
          />
        </div>
      </div>
    </div>
  );
};

export { AIAssistantPanel };

export default AIAssistantPanel;
